import { useState } from 'react';

import type { EcommerceCompany } from '@/api/ecommerce';
import SuitcaseIcon from '@/assets/suitcase.svg?react';
import { ConditionalRender } from '@/components/ui/ConditionalRender';

import {
  applyCompanyInvoice,
  clearCompanyData,
  type CompanyInvoiceValues,
  companyToInvoice,
  emptyCompanyInvoice,
  isCompanyInvoiceComplete,
  normalizeTaxId,
  pickCompanyInvoice,
} from '../helpers/companyInvoice';
import type { EcommerceFormValues } from '../schema';
import CompanyDataCard from './CompanyDataCard';
import CompanyDataModal, {
  type CompanyDataModalVariant,
} from './CompanyDataModal';
import CompanyLookupField, {
  type ManualEntryReason,
} from './CompanyLookupField';
import { COMPANY_NAME_NAME } from './InputCompanyName';
import { TAX_ID_NAME } from './InputTaxId';
import { IS_COMPANY_NAME } from './RadioGroupBuyerType';
import { useLingui } from '@lingui/react';
import { useController, useFormContext } from 'react-hook-form';

interface ModalState {
  key: number;
  open: boolean;
  variant: CompanyDataModalVariant;
  values: CompanyInvoiceValues;
}

const CompanyInvoiceSection = () => {
  const { i18n } = useLingui();
  const form = useFormContext<EcommerceFormValues>();
  const [isFromGus, setFromGus] = useState(false);
  const [shouldFocusTaxId, setShouldFocusTaxId] = useState(false);
  const [modal, setModal] = useState<ModalState>({
    key: 0,
    open: false,
    variant: 'edit',
    values: emptyCompanyInvoice(''),
  });

  // Pola faktury nie są inputami w formularzu kroku (karta / modal), więc
  // wymóg kompletnych danych firmy waliduje się na company_name przy
  // przejściu dalej — nie na blurze NIP-u, który przesuwałby przycisk.
  const {
    fieldState: { error: companyDataError },
  } = useController({
    control: form.control,
    name: COMPANY_NAME_NAME,
    rules: {
      validate: (_, values) => {
        if (!values.is_company || isCompanyInvoiceComplete(values)) return true;
        return values.company_name?.trim()
          ? i18n.t('Uzupełnij brakujące dane firmy — kliknij „Edytuj dane”')
          : i18n.t('Kliknij „Pobierz dane”, żeby uzupełnić dane firmy');
      },
    },
  });

  const invoice = pickCompanyInvoice(form.watch());
  const hasCompanyData = !!invoice.company_name.trim();

  const openModal = (
    variant: CompanyDataModalVariant,
    values: CompanyInvoiceValues
  ) => {
    setModal((prev) => ({ key: prev.key + 1, open: true, variant, values }));
  };

  const closeModal = () => {
    setModal((prev) => ({ ...prev, open: false }));
  };

  const saveCompany = (values: CompanyInvoiceValues, fromGus: boolean) => {
    applyCompanyInvoice(form, values);
    setFromGus(fromGus);
    closeModal();
  };

  const handleFound = (company: EcommerceCompany) => {
    const values = companyToInvoice(company);
    if (isCompanyInvoiceComplete(values)) {
      saveCompany(values, true);
      return;
    }
    openModal('incomplete', values);
  };

  const handleManualEntry = (reason: ManualEntryReason) => {
    openModal(
      reason,
      emptyCompanyInvoice(normalizeTaxId(form.getValues(TAX_ID_NAME) ?? ''))
    );
  };

  const handleChangeTaxId = () => {
    clearCompanyData(form);
    setFromGus(false);
    setShouldFocusTaxId(true);
  };

  const handleModalSave = (values: CompanyInvoiceValues) => {
    const isGusData =
      modal.variant === 'incomplete' || (modal.variant === 'edit' && isFromGus);
    saveCompany(values, isGusData);
  };

  return (
    <ConditionalRender when={(val) => val[IS_COMPANY_NAME] === true}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <SuitcaseIcon width={16} className="shrink-0 text-primary" />
          <p className="h5 text-dark-gray-500">{i18n.t('Dane do faktury')}</p>
        </div>

        {hasCompanyData ? (
          <CompanyDataCard
            values={invoice}
            isFromGus={isFromGus}
            error={companyDataError?.message}
            onEdit={() => openModal('edit', invoice)}
            onChangeTaxId={handleChangeTaxId}
          />
        ) : (
          <CompanyLookupField
            autoFocus={shouldFocusTaxId}
            error={companyDataError?.message}
            onFound={handleFound}
            onManualEntry={handleManualEntry}
          />
        )}
      </div>

      <CompanyDataModal
        key={modal.key}
        open={modal.open}
        variant={modal.variant}
        initialValues={modal.values}
        onClose={closeModal}
        onSave={handleModalSave}
      />
    </ConditionalRender>
  );
};

export default CompanyInvoiceSection;
